/**
 * ArtefactBlock - Unified artefact display component (v2)
 *
 * A simplified, animated component for displaying generated artefacts
 * like PDFs, documents, and patient handouts.
 *
 * Design principles:
 * - Single action link (preview prioritized over download)
 * - Decorative illustration animates in after generation completes
 * - Minimal metadata - just title and action
 * - Two variants: compact (default) and expanded (with metadata)
 * - Surface-agnostic: no knowledge of any host canvas/panel. The click
 *   either calls the caller-supplied `onPreview` or falls back to opening
 *   the export `url` — each chat surface decides what "preview" means
 *   (e.g. the Evidence chat routes pptx to the Slide Builder panel it
 *   mounts; Heidi Work has no such panel, so pptx downloads instead).
 */

import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { AlertCircle, ChevronRight, Download, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';
import { isPreviewableArtefactType, type ArtefactType } from '../../contracts';

import { artefactBlockMessages } from './artefact-block.messages';
import { ArtefactIllustration } from './ArtefactIllustration';

export interface ArtefactBlockProps {
  /** Type of artefact */
  artefactType: ArtefactType;
  /** Current status */
  status: 'generating' | 'completed' | 'error';
  /** Display title */
  title: string;
  /** Download/preview URL when completed */
  url?: string;
  /** Error message when status is 'error' */
  error?: string;
  /** Show expanded variant with metadata */
  showMetadata?: boolean;
  /** Optional metadata description */
  metadataDescription?: string;
  /** Callback when preview is requested (previewable types only) */
  onPreview?: () => void;
  /** Additional CSS classes */
  className?: string;
}

/** Map artefact type to user-friendly action label */
const getActionLabel = (type: ArtefactType): MessageDescriptor => {
  switch (type) {
    case 'pdf':
    case 'patient_handout':
    case 'form_fill':
      return artefactBlockMessages.previewPdf;
    case 'csv':
    case 'excel':
      return artefactBlockMessages.previewSpreadsheet;
    case 'pptx':
      return artefactBlockMessages.previewPresentation;
    case 'word':
      return artefactBlockMessages.downloadDocument;
    default:
      return artefactBlockMessages.download;
  }
};

/** Map artefact type to generating message */
const getGeneratingMessage = (type: ArtefactType): MessageDescriptor => {
  switch (type) {
    case 'pdf':
      return artefactBlockMessages.generatingPdf;
    case 'patient_handout':
      return artefactBlockMessages.generatingPatientHandout;
    case 'form_fill':
      return artefactBlockMessages.fillingPdfForm;
    case 'word':
      return artefactBlockMessages.generatingDocument;
    case 'csv':
      return artefactBlockMessages.generatingCsv;
    case 'excel':
      return artefactBlockMessages.generatingSpreadsheet;
    case 'pptx':
      return artefactBlockMessages.generatingPresentation;
    default:
      return artefactBlockMessages.generating;
  }
};

/**
 * ArtefactBlock - Displays artefact generation status with animation
 *
 * @example
 * <ArtefactBlock
 *   artefactType="pdf"
 *   status="completed"
 *   title="Clinical Summary"
 *   url="/api/preview/summary.pdf"
 *   onPreview={() => openPreviewPanel()}
 * />
 */
export const ArtefactBlock = ({
  artefactType,
  status,
  title,
  url,
  error,
  showMetadata = false,
  metadataDescription,
  onPreview,
  className,
}: ArtefactBlockProps): JSX.Element => {
  const intl = useIntl();
  const [showIllustration, setShowIllustration] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Animate illustration in after a delay when completed
  useEffect(() => {
    if (status === 'completed') {
      const timer = setTimeout(() => {
        setShowIllustration(true);
      }, 300); // 300ms delay before illustration appears
      return () => clearTimeout(timer);
    } else {
      setShowIllustration(false);
    }
  }, [status]);

  const isPreviewable = isPreviewableArtefactType(artefactType);

  const e2ePdfUrlAttr =
    typeof window !== 'undefined' &&
    (window as Window & { __E2E_HOOKS__?: boolean }).__E2E_HOOKS__ === true &&
    url
      ? { 'data-pdf-url': url }
      : undefined;

  const handleAction = () => {
    if (isPreviewable && onPreview) {
      onPreview();
    } else if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // === GENERATING STATE ===
  if (status === 'generating') {
    return (
      <div
        className={cn(
          'flex w-full items-center gap-3 rounded-lg border border-border bg-surface-accent p-4',
          'transition-all duration-200',
          className
        )}
        data-testid="artefact-block"
        data-status="generating"
        data-artefact-type={artefactType}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyP2 className="truncate font-medium text-text-primary">
            {title}
          </TypographyP2>
          <div className="flex items-center gap-1.5">
            <Loader2
              className="size-3 animate-spin text-text-tertiary"
              data-testid="loading-spinner"
            />
            <TypographyCaption className="text-text-tertiary">
              {intl.formatMessage(getGeneratingMessage(artefactType))}
            </TypographyCaption>
          </div>
        </div>
      </div>
    );
  }

  // === ERROR STATE ===
  if (status === 'error') {
    return (
      <div
        className={cn(
          'flex w-full items-center gap-3 rounded-lg border border-validation-destructive-border bg-validation-destructive-muted p-4',
          'transition-all duration-200',
          className
        )}
        data-testid="artefact-block"
        data-status="error"
        data-artefact-type={artefactType}
      >
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <TypographyP2 className="truncate font-medium text-text-primary">
            {title}
          </TypographyP2>
          <div className="flex items-center gap-1.5">
            <AlertCircle
              className="size-3 text-validation-destructive"
              data-testid="error-icon"
            />
            <TypographyCaption className="text-validation-destructive">
              {error || intl.formatMessage(artefactBlockMessages.generationFailed)}
            </TypographyCaption>
          </div>
        </div>
      </div>
    );
  }

  // === COMPLETED STATE ===
  return (
    <div
      className={cn(
        'group relative flex w-full items-center overflow-hidden rounded-lg border p-4',
        'cursor-pointer transition-all duration-200',
        'bg-surface-accent hover:bg-surface-accent-hover',
        'border-border hover:border-border-hover',
        className
      )}
      data-testid="artefact-block"
      data-status="completed"
      data-artefact-type={artefactType}
      {...e2ePdfUrlAttr}
      onClick={handleAction}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleAction();
        }
      }}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <TypographyP2 className="line-clamp-2 max-w-48 font-medium text-text-primary sm:max-w-xs md:max-w-md lg:max-w-lg">
          {title}
        </TypographyP2>

        {/* Metadata row (optional) */}
        {showMetadata && metadataDescription && (
          <TypographyCaption className="line-clamp-2 max-w-48 text-text-tertiary sm:max-w-xs md:max-w-md lg:max-w-lg">
            {metadataDescription}
          </TypographyCaption>
        )}

        {/* Action link indicator - changes to primary on card hover.
            Shown when clicking will do something: a caller-supplied preview
            handler (which may not need a url — e.g. the Evidence chat's
            Slide Builder preview) or a download url to open. */}
        {(url || (isPreviewable && onPreview)) && (
          <div
            className={cn(
              'flex w-fit items-center gap-0.5 text-text-secondary group-hover:text-text-primary',
              showMetadata && metadataDescription && 'mt-6'
            )}
            data-testid="action-button"
          >
            <TypographyCaption className="text-inherit">
              {intl.formatMessage(getActionLabel(artefactType))}
            </TypographyCaption>
            {isPreviewable ? (
              <ChevronRight className="size-3 rtl:-scale-x-100" />
            ) : (
              <Download className="size-3" />
            )}
          </div>
        )}
      </div>

      <div
        className={cn(
          'absolute right-8 top-4',
          'transition-all duration-300',
          showIllustration ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0',
          isHovered && showIllustration && 'scale-110'
        )}
        style={{
          transitionTimingFunction: 'cubic-bezier(.215, .61, .355, 1)',
        }}
        aria-hidden="true"
      >
        <ArtefactIllustration type={artefactType} className="h-auto w-[180px]" />
      </div>
    </div>
  );
};
